import { useMemo, useState } from 'react';

import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Ban, ChevronDown } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import {
  type GetListingQuickActionsDataQuery,
  type ListingStatusSelectFragment,
  Lots_Status_Enum_Enum,
} from '@graphql/__generated__/graphql';
import {
  type FormDefinitionType,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import { useFsboBlockDialog } from '@modules/_shared/components/listing-overview/FsboBlockDialogContext';
import {
  getLotStatusLabel,
  getLotStatusTagVariant,
} from '@modules/_shared/components/LotCardInfo';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  type RejectionReasonCode,
  parseLotStatus,
} from '@modules/_shared/utils/lotChangeExtraData';
import { buildLotChangeActivities } from '@modules/_shared/utils/lotChangeRules';
import { GET_DASHBOARD_LISTINGS } from '@modules/dashboard/dashboardQueries';
import { UPDATE_LISTING_TRACKED } from '@modules/listings/lotsQueries';
import { type Translate, useLocale } from '@shared/hooks/locale';
import { borderRadius } from '@shared/theme/StyleProvider';

type RejectionFormData = {
  reason_code: string | null;
  seller_message: string | null;
  internal_note: string | null;
};

// `onBlockInstead`: FSBO listings only, tenant admins only (#24256). A
// rejection tells the seller why; a scam operator must not be told
// anything, so the dialog offers the shadow ban as the way out instead.
const rejectionFormDefinition =
  (
    onBlockInstead: (() => void) | null,
  ): FormDefinitionType<RejectionFormData> =>
  ({ t }) =>
    [
      {
        type: 'select',
        name: 'reason_code',
        required: true,
        label: t('Reason for rejection'),
        options: () => [
          { value: 'incomplete_photos', label: t('Incomplete photos') },
          { value: 'price_unrealistic', label: t('Price unrealistic') },
          { value: 'duplicate_listing', label: t('Duplicate listing') },
          { value: 'policy_violation', label: t('Policy violation') },
          { value: 'scam', label: t('Scam or fraud') },
          { value: 'other', label: t('Other') },
        ],
        gridProps: { md: 12 },
      },
      {
        type: 'text',
        name: 'seller_message',
        required: true,
        multiline: true,
        label: t('Message to seller'),
        helpText: t(
          'Shown to the seller in the rejection email and push notification.',
        ),
        gridProps: { md: 12 },
      },
      {
        type: 'text',
        name: 'internal_note',
        multiline: true,
        label: t('Internal note (optional)'),
        helpText: t('Visible to staff only — not shown to the seller.'),
        gridProps: { md: 12 },
      },
      ...(onBlockInstead != null
        ? [
            {
              type: 'custom' as const,
              name: 'block_instead',
              gridProps: { md: 12 },
              element: (
                <Box>
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    startIcon={<Ban size={14} />}
                    onClick={onBlockInstead}
                    sx={{ ml: -1 }}
                  >
                    {t('Block this advertiser instead of rejecting')}
                  </Button>
                  <Typography
                    variant="caption"
                    component="div"
                    color="text.secondary"
                  >
                    {t(
                      'Shadow ban: nothing is sent to them, this and all their listings stay in pending review and never go live.',
                    )}
                  </Typography>
                </Box>
              ),
            },
          ]
        : []),
    ];

export const LISTING_STATUS_SELECT_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingStatusSelect on lots {
    id
    status
    tenant_id
  }
`);

const translateListingStatuses = (
  statuses: GetListingQuickActionsDataQuery['lots_status_enum'],
  t: Translate,
) =>
  statuses.map(status => ({
    id: status.value as Lots_Status_Enum_Enum,
    label: getLotStatusLabel(status.value, t),
  }));

interface ListingStatusSelectProps {
  listing?: ListingStatusSelectFragment | null;
  allStatuses?: GetListingQuickActionsDataQuery['lots_status_enum'];
  onListingStatusUpdated?: (status: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

export const ListingStatusSelect: React.FC<ListingStatusSelectProps> = ({
  listing,
  onListingStatusUpdated,
  allStatuses,
  isLoading = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [updateLot, { loading, error, reset }] = useMutation(
    UPDATE_LISTING_TRACKED,
    { refetchQueries: [GET_DASHBOARD_LISTINGS] },
  );
  const { me } = useAppData();
  const { t } = useLocale();
  const navigate = useNavigate();
  // The block dialog belongs to the listing page's advertiser card; when it
  // is on offer, the reject dialog hands over to it.
  const blockDialog = useFsboBlockDialog();
  const onBlockInstead =
    blockDialog?.available === true
      ? () => {
          setRejectionDialogOpen(false);
          blockDialog.openDialog();
        }
      : null;

  const translatedStatuses = useMemo(
    () => translateListingStatuses(allStatuses ?? [], t),
    [allStatuses, t],
  );

  const callUpdateLot = (
    lot: ListingStatusSelectFragment,
    set: { status: Lots_Status_Enum_Enum },
    activities: ReturnType<typeof buildLotChangeActivities>,
  ) =>
    updateLot({
      variables: {
        id: lot.id,
        set,
        activities,
        hasActivities: activities.length > 0,
      },
      onCompleted: data => {
        if (data?.update_lots_by_pk != null) {
          onListingStatusUpdated?.(set.status);
        }
      },
    });

  const onListingStatusChange = (status: Lots_Status_Enum_Enum) => {
    if (loading || listing == null || status === listing.status) {
      return;
    }

    if (status === 'rejected') {
      setRejectionDialogOpen(true);
      return;
    }

    if (status === 'won') {
      searchParams.set('selected-listing', listing.id);
      navigate({
        pathname: `/listings/${listing.id}/transaction/new/listing`,
        search: searchParams.toString(),
      });
    }

    const set = { status };
    const activities = buildLotChangeActivities(
      { status: parseLotStatus(listing.status) },
      set,
      {
        lotId: listing.id,
        tenantId: listing.tenant_id,
        sessionUserId: (!me?.is_admin ? null : me?.id) ?? null,
      },
    );

    callUpdateLot(listing, set, activities);
  };

  return isLoading ? (
    <Skeleton width={100} variant="rounded">
      <Button size="small" endIcon={<ChevronDown size={16} />}>
        placeholder
      </Button>
    </Skeleton>
  ) : (
    <>
      <FormControl
        size="small"
        sx={{
          width: 'auto',
          flex: '0 0 auto',
          display: 'inline-flex',
        }}
      >
        <Select
          size="small"
          variant="outlined"
          value={listing?.status ?? ''}
          onChange={e =>
            onListingStatusChange(e.target.value as Lots_Status_Enum_Enum)
          }
          disabled={disabled || loading || listing == null}
          displayEmpty
          id="listing-statuses-button"
          IconComponent={props =>
            loading ? (
              <CircularProgress
                disableShrink
                size={16}
                sx={{ color: 'text.primary' }}
                {...props}
              />
            ) : (
              <ChevronDown size={16} {...props} />
            )
          }
          renderValue={selected => {
            if (!selected) {
              return '';
            }

            return (
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ alignItems: 'center', minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: `${getLotStatusTagVariant(selected)}.main`,
                  }}
                />
                <Box component="span" sx={{ minWidth: 0 }}>
                  {getLotStatusLabel(selected, t)}
                </Box>
              </Stack>
            );
          }}
          sx={{
            width: 'max-content',
            maxWidth: '100%',
            height: 32,
            fontWeight: 600,
            fontSize: '0.875rem',
            borderRadius: borderRadius.medium,
            border: `1px solid ${(theme.vars ?? theme).palette.grey[300]}`,
            backgroundColor: (theme.vars ?? theme).palette.background.paper,
            color: 'text.primary',
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: (theme.vars ?? theme).palette.grey[400],
              backgroundColor: (theme.vars ?? theme).palette.grey[50],
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiSelect-select': {
              py: 0,
              pl: 1.5,
              pr: 3.5,
              height: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              width: 'auto',
              minWidth: 'unset',
            },
            '& .MuiSelect-icon': {
              color: 'text.primary',
              right: 10,
              top: 'calc(50% - 8px)',
            },
            '&.Mui-disabled': {
              borderColor: (theme.vars ?? theme).palette.grey[200],
              backgroundColor: (theme.vars ?? theme).palette.grey[50],
              color: (theme.vars ?? theme).palette.grey[400],
            },
          }}
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            slotProps: {
              paper: {
                sx: { mt: 0.5, minWidth: 'auto' },
              },
              list: {
                dense: true,
                'aria-labelledby': 'listing-statuses-button',
              },
            },
          }}
        >
          {translatedStatuses.map(({ id, label }) => (
            <MenuItem key={id} value={id}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Dialog
        open={rejectionDialogOpen}
        fullWidth
        maxWidth="sm"
        onClose={() => setRejectionDialogOpen(false)}
        sx={{
          '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.3)' },
        }}
        slots={{
          transition: SlideUpTransition,
        }}
      >
        <DialogTitle>{t('Reject listing')}</DialogTitle>
        <RaForm
          formDefinition={rejectionFormDefinition(onBlockInstead)}
          defaultValues={{}}
          onCancel={() => setRejectionDialogOpen(false)}
          onSubmit={async values => {
            if (loading) {
              return;
            }
            if (listing == null) {
              setRejectionDialogOpen(false);
              return;
            }
            const set = { status: Lots_Status_Enum_Enum.Rejected };
            const activities = buildLotChangeActivities(
              { status: parseLotStatus(listing.status) },
              set,
              {
                lotId: listing.id,
                tenantId: listing.tenant_id,
                sessionUserId: me?.id ?? null,
                changeContext: values.reason_code
                  ? {
                      rejection: {
                        reason_code: values.reason_code as RejectionReasonCode,
                        seller_message: values.seller_message ?? undefined,
                        internal_note: values.internal_note ?? undefined,
                      },
                    }
                  : undefined,
              },
            );
            await callUpdateLot(listing, set, activities);
            setRejectionDialogOpen(false);
          }}
        />
      </Dialog>
      <MutationErrorSnackbar error={error ?? null} onClose={reset} />
    </>
  );
};
